import { posthog } from 'posthog-js';
import { appsflyerEvents } from './appsflyerEvents';

// Type definitions for analytics events
interface OnboardingMotivationEvent {
  motivation: string;
  label: string;
  isSelected: boolean;
  totalSelected: number;
  allSelected: string[];
}

interface OnboardingJustBrowsingEvent {
  action: 'just_browsing';
  selectedMotivations: string[];
  totalSelected: number;
}

interface OnboardingSymbolEvent {
  symbol: string;
  name?: string;
  action: 'add' | 'remove';
  totalSymbols: number;
  allSymbols: string[];
}

interface OnboardingNavigationEvent {
  step: number;
  stepName: string;
  direction: 'next' | 'back';
  totalSteps: number;
}

// Analytics utility functions for onboarding flow
export const trackOnboardingMotivation = (eventData: OnboardingMotivationEvent) => {
  posthog.capture('Onboarding Motivation Selected', eventData);
  // Also track to AppsFlyer (fire-and-forget for performance)
  appsflyerEvents.onboarding.motivationSelected(eventData.motivation, eventData.totalSelected).catch(console.error);
};

export const trackOnboardingJustBrowsing = (eventData: OnboardingJustBrowsingEvent) => {
  posthog.capture('Onboarding Just Browsing Selected', eventData);
};

export const trackOnboardingSymbol = (eventData: OnboardingSymbolEvent) => {
  const eventName = eventData.action === 'add' ? 'Onboarding Symbol Added' : 'Onboarding Symbol Removed';
  posthog.capture(eventName, eventData);
  // Also track to AppsFlyer (fire-and-forget for performance)
  appsflyerEvents.onboarding
    .symbolSelected(eventData.symbol, eventData.action, eventData.totalSymbols)
    .catch(console.error);
};

export const trackOnboardingNavigation = (eventData: OnboardingNavigationEvent) => {
  posthog.capture('Onboarding Navigation', eventData);
  // Also track to AppsFlyer (fire-and-forget for performance)
  appsflyerEvents.onboarding.stepNavigation(eventData.step, eventData.direction).catch(console.error);
};

export const trackOnboardingNoHoldings = () => {
  posthog.capture('Onboarding No Holdings Selected', {
    action: 'no_holdings',
    timestamp: Date.now(),
  });
};

export const trackOnboardingCompletion = (completionData: {
  totalSteps: number;
  selectedMotivations: string[];
  selectedSymbols: string[];
  completionTime: number;
}) => {
  posthog.capture('Onboarding Completed', completionData);
  // Also track to AppsFlyer (fire-and-forget for performance)
  appsflyerEvents.onboarding
    .completed(completionData.selectedMotivations, completionData.selectedSymbols, completionData.completionTime)
    .catch(console.error);
};

// Error tracking for onboarding
export const trackOnboardingError = (error: {
  step: string;
  errorMessage: string;
  errorType: 'validation' | 'network' | 'component' | 'unknown';
  context?: Record<string, any>;
}) => {
  posthog.capture('Onboarding Error', {
    ...error,
    timestamp: Date.now(),
  });
};

// Try Bloom step analytics
interface TryBloomQuestionEvent {
  question: string;
  questionType: 'suggested' | 'custom';
  primarySymbol: string;
  totalSymbols: number;
}

interface TryBloomResponseEvent {
  question: string;
  questionType: 'suggested' | 'custom';
  responseTimeMs: number;
  hasToolOutputs: boolean;
}

interface TryBloomSkippedEvent {
  hadInteraction: boolean;
}

export const trackTryBloomQuestionAsked = (eventData: TryBloomQuestionEvent) => {
  posthog.capture('Try Bloom Question Asked', eventData);
};

export const trackTryBloomResponseReceived = (eventData: TryBloomResponseEvent) => {
  posthog.capture('Try Bloom Response Received', eventData);
};

export const trackTryBloomSkipped = (eventData: TryBloomSkippedEvent) => {
  posthog.capture('Try Bloom Skipped', eventData);
};
